// AUTOFILL1: password manager and browser autofill support, shared by every console.
//
// A password manager writes whatever it has on file into a select: "North Carolina", "NC" or "nc". A native select
// only accepts the exact option value, so anything else leaves it blank. bindAutofillSelect makes a select accept
// the option's value, its visible text, the name part of "NC - North Carolina", and either case, and always keeps
// the option VALUE (the two-letter code) as the stored value.

/** Extra spellings a password manager may write for a country option, keyed by the option value. */
const COUNTRY_ALIASES: Readonly<Record<string, readonly string[]>> = {
  US: ["usa", "u s", "u s a", "united states of america", "united states", "america"],
};

function squash(text: string): string {
  return text
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** The option value an autofilled string means, or null when it names no option. The empty option never matches. */
export function resolveAutofillOption(select: HTMLSelectElement, raw: string): string | null {
  const wanted = squash(raw);
  if (wanted === "") {
    return null;
  }
  const options = Array.from(select.options).filter((option) => option.value !== "");
  for (const option of options) {
    if (squash(option.value) === wanted) {
      return option.value;
    }
  }
  for (const option of options) {
    const text = squash(option.textContent ?? "");
    const parts = (option.textContent ?? "").split(" - ").map(squash);
    const aliases = COUNTRY_ALIASES[option.value.toUpperCase()] ?? [];
    if (text === wanted || parts.includes(wanted) || aliases.includes(wanted)) {
      return option.value;
    }
  }
  return null;
}

type SelectValueDescriptor = {
  get(this: HTMLSelectElement): string;
  set(this: HTMLSelectElement, value: string): void;
};

const nativeValue = Object.getOwnPropertyDescriptor(
  HTMLSelectElement.prototype,
  "value",
) as SelectValueDescriptor;

/**
 * Makes `select` accept autofilled state or country text (full name, code, any case). Safe to call twice on one
 * select, and to call before its options exist: matching reads the options at the moment a value is written.
 * Writing the exact option value, or "", behaves as a native select does.
 */
export function bindAutofillSelect<T extends HTMLSelectElement>(select: T): T {
  if (Object.prototype.hasOwnProperty.call(select, "value")) {
    return select;
  }
  Object.defineProperty(select, "value", {
    configurable: true,
    enumerable: true,
    get(this: HTMLSelectElement): string {
      return nativeValue.get.call(this);
    },
    set(this: HTMLSelectElement, next: string): void {
      const exact = Array.from(this.options).some((option) => option.value === next);
      const resolved = exact ? next : resolveAutofillOption(this, next);
      nativeValue.set.call(this, resolved ?? next);
    },
  });
  return select;
}

/**
 * Autocomplete token per legal-details field (softwareOwnerLegal.ts). The registered, signatory and billing groups
 * carry their own section- prefix so a manager never fills one group from another's data. The EIN is a tax
 * identifier and is never autofilled; the formation state is a legal fact, not an address, so it stays unfilled
 * but still accepts a state name or code.
 */
export const LEGAL_AUTOCOMPLETE: Readonly<Record<string, string>> = {
  legalName: "organization",
  dba: "off",
  ein: "off",
  stateOfFormation: "off",
  regStreet: "section-registered street-address",
  regCity: "section-registered address-level2",
  regState: "section-registered address-level1",
  regZip: "section-registered postal-code",
  primaryName: "name",
  primaryTitle: "organization-title",
  primaryPhone: "tel",
  primaryEmail: "email",
  signatoryName: "section-signatory name",
  signatoryTitle: "section-signatory organization-title",
  billingContactName: "section-billing name",
  billingContactEmail: "section-billing email",
  billingContactPhone: "section-billing tel",
  operationalContactName: "section-operational name",
  billStreet: "section-billing street-address",
  billCity: "section-billing address-level2",
  billState: "section-billing address-level1",
  billZip: "section-billing postal-code",
};
